/**
 * Animate elements when scrolled into view using Intersection Observer
 * (toggles the .animate class on the element)
 *
 * - Animations trigger once by default
 * - Add `.animate-repeat` to re-animate whenever the element scrolls
 *   in or out of view
 */

import { StatBox } from './callout-stat';

class AnimateInView {
	constructor(el) {
		this.el = el;
		this.animateOnce = !this.el.classList.contains('animate-repeat');
		this.hasAnimated = false;
		this.isCurrentlyAnimated = false; // Track current animation state
		this.lastToggleTime = 0;          // Track last toggle to prevent rapid changes
		this.removeTimeout = null;        // Track pending remove operation
		this.cooldownPeriod = 150;        // Minimum ms between toggles

		// Check if Intersection Observer is supported
		if (!('IntersectionObserver' in window)) {
			// Fallback to scroll event for older browsers
			this.setupScrollListener();
			return;
		}

		// If element starts inside a hidden container (e.g. off-canvas panel),
		// defer setup until the container becomes visible.
		const ariaHiddenParent = el.closest('[aria-hidden="true"]');
		if (ariaHiddenParent) {
			this.watchForContainerOpen(ariaHiddenParent);
			return;
		}

		this.setupIntersectionObserver();
	}

	// Shared animation trigger used by IO, initial check, and container-open watcher.
	triggerAnimation() {
		if (this.el.hasAttribute('data-stat') && !this.el.classList.contains('animate')) {
			if (this.hasAnimated) {
				const numberEl = this.el.querySelector('.callout-stat-number');
				if (numberEl) numberEl.textContent = '0';
			}
			new StatBox(this.el, { delay: 0 });
		}
		this.el.classList.add('animate');
		this.isCurrentlyAnimated = true;
		this.hasAnimated = true;
		this.lastToggleTime = Date.now();
		if (this.animateOnce && this.observer) this.observer.disconnect();
	}

	// Watch for a hidden aria-hidden ancestor to open, then animate after its
	// transition completes (prevents animating while the panel is still sliding in).
	watchForContainerOpen(parent) {
		const mo = new MutationObserver(() => {
			if (parent.getAttribute('aria-hidden') === 'true') return;
			mo.disconnect();

			// Read panel transition time (Foundation uses data-transition-time="300ms")
			const raw = parent.dataset.transitionTime || '0';
			const delay = parseFloat(raw) || 300;

			setTimeout(() => {
				if (this.hasAnimated && this.animateOnce) return;
				const rect = this.el.getBoundingClientRect();
				if (rect.top < window.innerHeight && rect.bottom > 0) {
					this.triggerAnimation();
				} else {
					// Element not in view yet — set up observer to catch it later
					this.setupIntersectionObserver();
				}
			}, delay);
		});
		mo.observe(parent, { attributes: true, attributeFilter: ['aria-hidden'] });
	}

	setupIntersectionObserver() {
		// Debounce to prevent rapid toggling
		let debounceTimeout = null;
		let lastIntersectingState = null;

		const observer = new IntersectionObserver((entries) => {
			const entry = entries[0];
			const isIntersecting = entry.isIntersecting;

			// Cancel any pending removal immediately — don't wait for the debounce,
			// as the removeTimeout could fire before it and wrongly clear .animate.
			if (isIntersecting && this.removeTimeout !== null) {
				clearTimeout(this.removeTimeout);
				this.removeTimeout = null;
			}

			// Ignore if state hasn't actually changed
			if (lastIntersectingState === isIntersecting) {
				return;
			}

			// Clear any pending debounce
			if (debounceTimeout !== null) {
				clearTimeout(debounceTimeout);
			}

			// Debounce the state change
			debounceTimeout = setTimeout(() => {
				const hasAnimateClass = this.el.classList.contains('animate');
				const now = Date.now();

				// Prevent rapid toggling with cooldown period ONLY for
				// entering / re-entering the viewport. Leaving the viewport
				// should always be processed so we reliably clear .animate.
				if (isIntersecting && (now - this.lastToggleTime < this.cooldownPeriod)) {
					debounceTimeout = null;
					return;
				}

				if (isIntersecting && !hasAnimateClass) {
					// Skip if element is inside a hidden container (e.g. off-canvas panel
					// mid-transition). watchForContainerOpen() handles those elements.
					if (this.el.closest('[aria-hidden="true"]')) {
						debounceTimeout = null;
						return;
					}

					// Element has entered the viewport (from any direction).
					// First entry should always animate. Subsequent entries
					// animate in either scroll direction as long as the element
					// is not marked `.animate-once`.
					const canAnimate =
						!this.hasAnimated ||
						!this.animateOnce;
					if (canAnimate) {
						this.triggerAnimation();
						lastIntersectingState = isIntersecting;
					}
				} else if (!isIntersecting && hasAnimateClass && !this.removeTimeout) {
					// Element is leaving view - reset animation (default behavior)
					if (!this.animateOnce) {
						// Clear any existing timeout
						if (this.removeTimeout !== null) {
							clearTimeout(this.removeTimeout);
						}

						// Delay removal to prevent flickering - only remove if truly out of view
						this.removeTimeout = setTimeout(() => {
							// Double-check element is still out of view before removing
							if (!this.el.classList.contains('animate')) {
								this.removeTimeout = null;
								return;
							}

							// Check if element is still out of view
							const rect = this.el.getBoundingClientRect();
							const isStillOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;

							if (isStillOutOfView) {
								this.el.classList.remove('animate');
								this.isCurrentlyAnimated = false;
								lastIntersectingState = false;
							}
							this.removeTimeout = null;
						}, 200); // 200ms delay before removing
					}
				}

				debounceTimeout = null;
			}, 50); // 50ms debounce
		}, {
			// Match scroll version: any part of element in viewport counts
			threshold: 0,
			rootMargin: '0px'
		});

		// Store observer for potential cleanup
		this.observer = observer;
		observer.observe(this.el);

		// Dynamic content may already be in view; check once after observe.
		// Use strict bounds (top < innerHeight AND bottom > 0) so elements at the exact
		// bottom edge of the viewport (e.g. just below a viewport-height banner) are not
		// prematurely animated before the user scrolls to them.
		const rect = this.el.getBoundingClientRect();
		const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
		if (inViewport && !this.el.classList.contains('animate')) {
			if (!this.hasAnimated || !this.animateOnce) {
				this.triggerAnimation();
			}
		}
	}

	// Fallback for browsers without Intersection Observer support
	setupScrollListener() {
		const $this = this;
		const windowHeight = window.innerHeight;

		// Debounce scroll handler to prevent rapid toggling
		let scrollTimeout;
		window.addEventListener('scroll', function () {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				const positionFromTop = $this.checkPosition($this.el, windowHeight);
				const shouldAnimate = positionFromTop - windowHeight <= 0;

				// Only toggle if state actually changed
				if (shouldAnimate && !$this.isCurrentlyAnimated) {
					// Add .animate class
					const canAnimate =
						!$this.hasAnimated ||
						!$this.animateOnce;
					if (canAnimate) {
						if ($this.el.hasAttribute("data-stat") && !$this.el.classList.contains('animate')) {
							if ($this.hasAnimated) {
								const numberEl = $this.el.querySelector('.callout-stat-number');
								if (numberEl) numberEl.textContent = '0';
							}
							new StatBox($this.el, { delay: 0 });
						}
						$this.el.classList.add('animate');
						$this.isCurrentlyAnimated = true;
						$this.hasAnimated = true;
					}
				}
				else if ($this.isCurrentlyAnimated) {
					// Reset animation if element is fully out of view and not animate-once
					const rect = $this.el.getBoundingClientRect();
					const isStillOutOfView = rect.bottom < 0 || rect.top > windowHeight;
					if (isStillOutOfView && !$this.animateOnce) {
						$this.el.classList.remove('animate');
						$this.isCurrentlyAnimated = false;
					}
				}
			}, 10); // Small debounce delay
		});
	}

	checkPosition(el) {
		return el.getBoundingClientRect().top;
	}
}

function initAnimateInViewForElement(el) {
	if (!el || el.__animateInViewInitialized) return;
	el.__animateInViewInitialized = true;
	new AnimateInView(el);
}

function initAnimateInView() {
	const animateElements = document.querySelectorAll('.animate-in-view');

	animateElements.forEach(el => new AnimateInView(el));
}

// Observe dynamically added elements (e.g. .animate-in-view, Clive-injected callout stats)
function observeAnimateInViewElements() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        // Node itself: .animate-in-view, or data-stat (callout stat), or Clive-injected
        const selfMatch =
          (node.classList && node.classList.contains('animate-in-view')) ||
          (node.hasAttribute('data-stat') && !node.classList.contains('animate')) ||
          node.hasAttribute('data-clive-element-id');
        if (selfMatch) initAnimateInViewForElement(node);
        // Descendants: .animate-in-view and [data-clive-element-id]
        if (node.querySelectorAll) {
          node.querySelectorAll('.animate-in-view').forEach(initAnimateInViewForElement);
          node.querySelectorAll('[data-clive-element-id]').forEach(initAnimateInViewForElement);
        }
      });
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

// Run on page load and start observing for dynamically added elements (e.g. Clive)
function startAnimateInView() {
	initAnimateInView();
	observeAnimateInViewElements();
}
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', startAnimateInView);
} else {
	startAnimateInView();
}

// Make globally available
window.AnimateInView = AnimateInView;

// Handle deferred initialization (for dynamically loaded content before animate-in-view is initialized)
const pending = window.__AnimateInViewPending;
if (Array.isArray(pending) && pending.length) {
  const els = pending.splice(0);
  els.forEach((el) => {
    if (el) new AnimateInView(el);
  });
}

export default AnimateInView;
